/**
 * new-hmp-design-product-spotlight.js
 * ---------------------------------------------------------------
 * AJAX "Add to Tote" for the "The edit" product cards.
 * Uses Shopify's native Ajax Cart API (/cart/add.js) -- no jQuery,
 * no third-party cart library.
 *
 * CART UI REFRESH -- delegates to your theme's existing myupdate():
 * Your theme already has a global `myupdate(variantid)` function
 * (defined inline elsewhere in the theme, e.g. cart-drawer/theme.liquid)
 * that owns ALL cart-UI refresh logic: header count bubble, cart drawer
 * item list, GWP state, free-sample logic, and the shipping progress
 * bar. This script does NOT reimplement any of that -- it just calls
 * your function after a successful add, the same way any other
 * "Add to cart" button in your theme presumably already does. If you
 * change myupdate() in the future, this section picks up that change
 * automatically with zero edits here -- that's the whole point.
 *
 * If your global function is ever renamed, set
 * data-cart-update-fn="yourFunctionName" on this script tag (see
 * new-hmp-design-product-spotlight.liquid) instead of editing this file.
 *
 * OPTIONAL: if opening the cart drawer after adding requires clicking
 * a trigger element elsewhere in your theme (rather than myupdate()
 * handling that itself), set data-open-cart-selector="<css selector>"
 * on the script tag and this file will click it for you.
 */
(function () {
  'use strict';

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function setBusy(form, busy) {
    var submit = form.querySelector('[data-spotlight-submit]');
    var label = form.querySelector('[data-spotlight-submit-label]');
    if (!submit) return;
    submit.disabled = busy;
    submit.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (label) {
      if (busy) {
        label.dataset.originalLabel = label.dataset.originalLabel || label.textContent;
        label.textContent = 'Adding...';
      } else if (label.dataset.originalLabel) {
        label.textContent = label.dataset.originalLabel;
      }
    }
  }

  function showError(form, message) {
    var errorEl = form.querySelector('[data-spotlight-error]');
    if (!errorEl) return;
    if (!message) {
      errorEl.hidden = true;
      errorEl.textContent = '';
      return;
    }
    errorEl.hidden = false;
    errorEl.textContent = message;
  }

  // Minimal, last-resort fallback ONLY used if the theme's own
  // myupdate() function can't be found -- keeps the button from
  // feeling broken, but intentionally does none of myupdate's richer
  // logic (GWP, samples, shipping bar) since duplicating that is
  // exactly what we're avoiding here.
  function fallbackCartCountUpdate() {
    fetch(routesRoot() + 'cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count], .header-cart-count').forEach(function (el) {
          el.textContent = cart.item_count > 0 ? cart.item_count : '';
        });
      })
      .catch(function () {});
  }

  function refreshCartUI(scriptTag, variantId) {
    var fnName = (scriptTag && scriptTag.getAttribute('data-cart-update-fn')) || 'myupdate';
    var fn = window[fnName];

    if (typeof fn === 'function') {
      fn(variantId);
    } else {
      console.warn(
        '[new-hmp-design] window.' + fnName + '() was not found -- falling back to a minimal cart-count update. ' +
        'If your theme\'s cart-refresh function has a different name, set data-cart-update-fn="yourFunctionName" ' +
        'on the new-hmp-design-product-spotlight.js script tag.'
      );
      fallbackCartCountUpdate();
    }

    var openSelector = scriptTag && scriptTag.getAttribute('data-open-cart-selector');
    if (openSelector) {
      var trigger = document.querySelector(openSelector);
      if (trigger) trigger.click();
    }

    // Still fire a lightweight event for anything else on the page that
    // wants to react to an add-to-cart from this section specifically --
    // this is additive, not a replacement for myupdate().
    window.dispatchEvent(new CustomEvent('new-hmp-design:cart:added', { detail: { variantId: variantId } }));
  }

  function handlePriceUpdate(form) {
    var select = form.querySelector('[data-spotlight-variant-select]');
    var priceEl = form.closest('[data-spotlight-product]').querySelector('[data-spotlight-price] .new-hmp-design-spotlight__price-amount');
    if (!select || select.tagName !== 'SELECT' || !priceEl) return;

    select.addEventListener('change', function () {
      var option = select.options[select.selectedIndex];
      var price = option.getAttribute('data-price');
      var submit = form.querySelector('[data-spotlight-submit]');
      if (option.disabled) {
        if (submit) {
          submit.disabled = true;
          var label = form.querySelector('[data-spotlight-submit-label]');
          if (label) label.textContent = 'Sold out';
        }
        return;
      }
      if (submit) {
        submit.disabled = false;
        var label2 = form.querySelector('[data-spotlight-submit-label]');
        if (label2 && label2.dataset.originalLabel) label2.textContent = label2.dataset.originalLabel;
      }
      if (price) {
        var cents = parseInt(price, 10);
        priceEl.textContent = formatMoney(cents);
      }
    });
  }

  function formatMoney(cents) {
    // Falls back to a simple format if Shopify.formatMoney isn't loaded.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function initForm(form, scriptTag) {
    if (form.dataset.hmpSpotlightInit === 'true') return; // avoid double-binding
    form.dataset.hmpSpotlightInit = 'true';
    handlePriceUpdate(form);

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      showError(form, null);
      setBusy(form, true);

      var formData = new FormData(form);
      var variantId = formData.get('id');
      var payload = {
        items: [
          {
            id: variantId,
            quantity: parseInt(formData.get('quantity') || '1', 10)
          }
        ]
      };

      fetch(routesRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) {
              var message = (data && data.description) || 'That item could not be added to your tote.';
              throw new Error(message);
            }
            return data;
          });
        })
        .then(function () {
          refreshCartUI(scriptTag, variantId);
        })
        .catch(function (err) {
          showError(form, err.message || 'Something went wrong. Please try again.');
        })
        .finally(function () {
          setBusy(form, false);
        });
    });
  }

  // Must capture synchronously here — document.currentScript is only
  // reliable during the script's own top-level execution (even when
  // deferred), not inside a later event-handler callback.
  var THIS_SCRIPT = document.currentScript;

  // THEME EDITOR NOTE: when a merchant adds, duplicates, or reorders this
  // section in the Shopify theme editor, the editor swaps in new section
  // HTML via AJAX rather than a full page reload. Shopify fires
  // `shopify:section:load` on the section's container specifically so
  // section scripts can re-run init against just that section — without
  // this, a freshly-added section's Add to Tote button would silently do
  // nothing until the merchant manually refreshes the editor preview.
  function initAll(root) {
    var forms = root.querySelectorAll ? root.querySelectorAll('[data-spotlight-form]') : [];
    forms.forEach(function (form) {
      initForm(form, THIS_SCRIPT);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  if (!window.__newHmpSpotlightListenerBound) {
    window.__newHmpSpotlightListenerBound = true;
    document.addEventListener('shopify:section:load', function (event) {
      initAll(event.target);
    });
  }
})();
